import { createContext, ReactElement, useCallback, useEffect, useState } from "react";
import {
  ICrew,
  ILanguage, IMessageContext,
  ISchemaNode,
  ISchemaNodePath,
  IShift,
  ISystemContextData,
  IUser,
  IUserConfig
} from "../types";
import { Alert } from "@mui/material";
import { executeMultiActions, selectedNode, setDarkMode, setDataContext } from "../actions";
import { useDispatch } from "react-redux";
import SkeletonApp from "../components/skeletonApp";
import { useAsyncFetch, useLazyFetch } from "../system/useFetch";
import {
  getCurrentLanguage,
  getDashboardID,
  setDefaultLocaleConfig,
  updateCurrentLanguage
} from "../configuration/configuration";
import { isAzureMode } from "../system/security";
import { IDashboard } from "../modules/interruptions/types";
import enLanguage from "../configuration/languages/lang-en.json";
import esLanguage from "../configuration/languages/lang-es.json";
import ptLanguage from "../configuration/languages/lang-pt.json";
import DialogMessage from "../components/dialogs/dialogMessage";

const languages = {
  "en-us": enLanguage,
  "es-mx": esLanguage,
  "pt-br": ptLanguage
};


type ISystemProviderProps = {
  children: ReactElement[] | ReactElement;
};

const emptySystemContextData: ISystemContextData = {
  schema: [],
  user: null,
  languageCode: "en-us",
  language: languages["en-us"],
  setLanguage: (languageCode: "es-mx" | "pt-br" | "en-us") => {
    console.log(languageCode);
  },
  shifts: [],
  crews: [],
  updateUserConfig: (config: IUserConfig) => console.log(config),
  showMessage: (messageContext: IMessageContext) => { }
};

let initialized = false;

const SystemContext = createContext<ISystemContextData>(emptySystemContextData);
const { Provider } = SystemContext;

const SystemContextProvider = ({ children }: ISystemProviderProps): ReactElement => {
  const dispatch = useDispatch<any>();
  const [getSystemData, { loading, error: errorGettingData, data }] = useLazyFetch<[ISchemaNode[], IUser, IShift[], ICrew[]]>();

  const [ schema, setSchema ] = useState<ISchemaNode[]>([]);
  const [ user, setUserConfig ] = useState<IUser | null>(null);
  const [ shifts, setShifts ] = useState<IShift[]>([]);
  const [ crews, setCrews ] = useState<ICrew[]>([]);
  const [ getNodePath ] = useAsyncFetch<ISchemaNodePath>();
  const [ languageCode, setLanguageCode ] = useState<"es-mx" | "pt-br" | "en-us">("en-us");
  const [ language, updateLanguage ] = useState<ILanguage>(languages["en-us"]);
  const [ getDashboard ] = useAsyncFetch<IDashboard>();
  const [messageContext, setMessageContext] = useState<IMessageContext | null>(null);

  const setLanguage = useCallback((langCode: "es-mx" | "pt-br" | "en-us") => {
    try {
      setLanguageCode(langCode);
      updateCurrentLanguage(languages[langCode]);
      updateLanguage(getCurrentLanguage());
    } catch (e) {
      console.error(e);
    }
  }, [setLanguageCode]);

  const updateUserConfig = useCallback((config: IUserConfig) => {
    const  newUser = Object.assign({}, user, { config });
    setUserConfig(newUser);
    setDefaultLocaleConfig(newUser.config.locale);
    dispatch(setDarkMode(newUser.config.darkMode));
    setLanguage(newUser.config.locale.language);
  }, [ user ]);

  const showMessage = useCallback((messageCxt: IMessageContext) => {
    setMessageContext(messageCxt);
  }, [ setMessageContext ]);

  useEffect(() => {
    if (initialized) {
      return;
    }

    if (getSystemData && !data) {
      getSystemData([
        {
          module: "system",
          query: "getSchema",
          payload: {}
        },
        {
          module: "security",
          query: "getUserConfig",
          payload: !isAzureMode()
            ? {
              user: {
                sub: parseInt(localStorage.getItem("userID") ?? "0", 10)
              }
            }
            : {}
        },
        {
          module: "production",
          query: "getShifts",
          payload: {}
        },
        {
          module: "production",
          query: "getCrews",
          payload: {}
        }]);
    } else if (data) {
      initialized = true;

      setSchema(data[0]);
      setUserConfig(data[1]);
      setShifts(data[2]);
      setCrews(data[3]);

      if (data[1]) {
        const userConfig = data[1].config;
        setDefaultLocaleConfig(userConfig.locale);
        setLanguage(userConfig.locale.language);

        const dashboardID = getDashboardID();

        if (dashboardID) {

          getDashboard({
            module: "dashboard",
            query: "getDashboard",
            payload: {
              dashboardID
            }
          }).then(dashboard => {

            if(dashboard){
              getNodePath({
                module: "system",
                query: "getNodePath",
                payload: {
                  nodeID: dashboard.nodeID,
                  nodeType: dashboard.nodeType
                }
              }).then(nodePath => {
                if (nodePath){
                  const factory = data[0].find(x => x.nodeID === nodePath.factoryID) ?? null;
                  const area  = factory ? factory.children?.find(x => x.nodeID === nodePath.areaID) : null;
                  const zone = area ? area.children?.find(x => x.nodeID === nodePath.zoneID) : null;

                  let node;

                  if (zone) {
                    node = zone;
                  } else if (area) {
                    node = area;
                  } else if (factory) {
                    node = factory;
                  }

                  if (node) {
                    dispatch(executeMultiActions([
                      setDarkMode(userConfig.darkMode),
                      selectedNode(
                        {
                          nodeID: node.nodeID,
                          code: node.code,
                          name: node.name,
                          color: node.color,
                          timezone: node.timezone,
                          type: node.type,
                          menu: node.menu,
                          indicator: node.indicator,
                          children: node.children
                        }),
                      setDataContext({
                        title: "Charts",
                        module: "reports",
                        subModule: "charts"
                      })
                    ]));
                  }else {
                    dispatch(setDarkMode(userConfig.darkMode));
                  }
                }
              });
            }else {
              dispatch(setDarkMode(userConfig.darkMode));
            }
          });
        } else if (userConfig.custom && userConfig.custom.defaultNode) {
          const factory = data[0].find(x => x.nodeID === userConfig.custom.defaultNode?.factory) ?? null;
          const area  = factory ? factory.children?.find(x => x.nodeID === userConfig.custom.defaultNode?.area) : null;
          const zone = area ? area.children?.find(x => x.nodeID === userConfig.custom.defaultNode?.zone) : null;

          let node;

          if (zone) {
            node = zone;
          } else if (area) {
            node = area;
          } else if (factory) {
            node = factory;
          }

          if (node) {
            dispatch(executeMultiActions([
              setDarkMode(userConfig.darkMode),
              selectedNode({
                nodeID: node.nodeID,
                code: node.code,
                name: node.name,
                color: node.color,
                timezone: node.timezone,
                type: node.type,
                menu: node.menu,
                indicator: node.indicator,
                children: node.children
              })]));
          } else {
            dispatch(setDarkMode(userConfig.darkMode));
          }
        } else {
          dispatch(setDarkMode(userConfig.darkMode));
        }
      }
    }
  }, [ data, dispatch, getSystemData ]);

  return (
    <Provider value={{
      schema,
      user,
      languageCode,
      language,
      setLanguage,
      shifts,
      crews,
      updateUserConfig,
      showMessage
    }}>
      {loading && <SkeletonApp/>}
      {errorGettingData && <Alert severity="warning">{errorGettingData.message}</Alert>}
      {data && children}
      <DialogMessage messageContext={messageContext} />
    </Provider>
  );
};

export { SystemContextProvider, SystemContext };
